import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Clock, Calendar, Building2, CreditCard, 
  CheckCircle, Send, Settings,
  FileText, History, Edit, Save, X, Zap, Bike
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PayoutManagement = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('restaurants');
  const [restaurants, setRestaurants] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [settings, setSettings] = useState({
    payout_day: 'monday',
    payout_time: '00:00',
    auto_payout_enabled: true,
    min_payout_amount: 10.0,
    notification_email: ''
  });
  const [loading, setLoading] = useState(false);
  const [processDialog, setProcessDialog] = useState({ open: false, payout: null });
  const [editDialog, setEditDialog] = useState({ open: false, restaurant: null });
  const [transferRef, setTransferRef] = useState('');
  const [editForm, setEditForm] = useState({});
  const [running, setRunning] = useState(false);
  const [driverPayouts, setDriverPayouts] = useState([]);

  const handleRunWeekly = async () => {
    if (!window.confirm(language === 'pt'
      ? 'Gerar payouts semanais agora? Vai criar entradas pendentes para todos os restaurantes e estafetas com pedidos entregues neste período.'
      : 'Generate weekly payouts now? Pending payouts will be created for every restaurant/driver with delivered orders in the period.')) return;
    setRunning(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/payouts/run-weekly`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(language === 'pt'
          ? `Gerados: ${data.restaurant_payouts?.length || 0} restaurantes, ${data.driver_payouts?.length || 0} estafetas`
          : `Generated: ${data.restaurant_payouts?.length || 0} restaurants, ${data.driver_payouts?.length || 0} drivers`);
        fetchData();
      } else {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || (language === 'pt' ? 'Erro ao gerar payouts' : 'Failed to generate payouts'));
      }
    } catch (e) {
      toast.error(language === 'pt' ? 'Erro de rede' : 'Network error');
    } finally {
      setRunning(false);
    }
  };

  const fetchDriverPayouts = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/driver-payouts/pending`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) setDriverPayouts(await res.json());
    } catch (e) { console.debug('fetchDriverPayouts failed', e); }
  }, [token]);

  const markDriverPaid = async (payoutId) => {
    const ref = window.prompt(language === 'pt' ? 'Referência da transferência (opcional):' : 'Transfer reference (optional):') || '';
    try {
      const res = await fetch(`${API_URL}/api/admin/driver-payouts/${payoutId}/mark-paid`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ transfer_reference: ref }),
      });
      if (res.ok) {
        toast.success(language === 'pt' ? 'Marcado como pago' : 'Marked as paid');
        fetchDriverPayouts();
      }
    } catch (e) { console.debug('markDriverPaid failed', e); }
  };
  
  // Date filters
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  const dayOptions = [
    { value: 'monday', label: language === 'pt' ? 'Segunda-feira' : 'Monday' },
    { value: 'tuesday', label: language === 'pt' ? 'Terça-feira' : 'Tuesday' },
    { value: 'wednesday', label: language === 'pt' ? 'Quarta-feira' : 'Wednesday' },
    { value: 'thursday', label: language === 'pt' ? 'Quinta-feira' : 'Thursday' },
    { value: 'friday', label: language === 'pt' ? 'Sexta-feira' : 'Friday' },
    { value: 'saturday', label: language === 'pt' ? 'Sábado' : 'Saturday' },
    { value: 'sunday', label: language === 'pt' ? 'Domingo' : 'Sunday' }
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [restaurantsRes, pendingRes, historyRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/restaurants`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/payouts/pending`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/payouts/history?limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/payout-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (restaurantsRes.ok) {
        const data = await restaurantsRes.json();
        setRestaurants(data);
      }
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingPayouts(data);
      }
      if (historyRes.ok) {
        const data = await historyRes.json();
        setPayoutHistory(data);
      }
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchData();
    fetchDriverPayouts();
  }, [fetchData, fetchDriverPayouts]);

  const handleSaveRestaurantIBAN = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/restaurants/${editForm.restaurant_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          iban: editForm.iban,
          company_name: editForm.company_name,
          tax_id: editForm.tax_id
        })
      });

      if (res.ok) {
        toast.success(language === 'pt' ? 'Dados bancários salvos!' : 'Bank details saved!');
        setEditDialog({ open: false, restaurant: null });
        fetchData();
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao salvar' : 'Error saving');
    }
  };

  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/payout-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success(language === 'pt' ? 'Configurações salvas!' : 'Settings saved!');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao salvar' : 'Error saving');
    }
  };

  const handleProcessPayout = async (payout) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/payouts/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          restaurant_id: payout.restaurant_id,
          amount: payout.amount,
          orders_count: payout.orders_count,
          transfer_reference: transferRef,
          notes: `Payout processed on ${new Date().toLocaleDateString()}`
        })
      });

      if (res.ok) {
        toast.success(language === 'pt' ? 'Pagamento processado!' : 'Payout processed!');
        setProcessDialog({ open: false, payout: null });
        setTransferRef('');
        fetchData();
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao processar' : 'Error processing');
    }
  };

  const formatIBAN = (iban) => {
    if (!iban) return '-';
    return `${iban.slice(0, 4)}****${iban.slice(-4)}`;
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      processing: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700'
    };
    const labels = {
      pending: language === 'pt' ? 'Pendente' : 'Pending',
      processing: language === 'pt' ? 'Processando' : 'Processing',
      completed: language === 'pt' ? 'Concluído' : 'Completed',
      failed: language === 'pt' ? 'Falhou' : 'Failed'
    };
    return <Badge className={colors[status] || colors.pending}>{labels[status] || status}</Badge>;
  };

  // Filter history by date
  const filteredHistory = payoutHistory.filter(p => {
    const pDate = new Date(p.created_at || p.processed_at);
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    to.setHours(23, 59, 59);
    return pDate >= from && pDate <= to;
  });

  const totalPending = pendingPayouts.reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalCompleted = filteredHistory.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount || 0), 0);
  const restaurantsWithIBAN = restaurants.filter(r => r.iban).length;

  return (
    <div className="space-y-6">
      {/* Top action bar — auto-payout runner */}
      <Card className="rounded-2xl bg-gradient-to-r from-[#FF6B35]/10 to-[#2EC4B6]/10 border-0">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <p className="font-bold text-[#004E64] flex items-center gap-2">
              <Zap size={18} className="text-[#FF6B35]" />
              {language === 'pt' ? 'Contabilidade Automática Semanal' : 'Automated Weekly Accounting'}
            </p>
            <p className="text-xs text-[#9FA2B4] mt-1">
              {language === 'pt'
                ? `Geração automática às ${settings.payout_time || '00:00'} de ${dayOptions.find(d => d.value === settings.payout_day)?.label || '...'} (15% de comissão WEGOO já deduzida).`
                : `Auto-runs at ${settings.payout_time || '00:00'} on ${dayOptions.find(d => d.value === settings.payout_day)?.label || '...'} (15% WEGOO commission already deducted).`}
            </p>
            {settings.last_payout_run && (
              <p className="text-xs text-[#9FA2B4] mt-1">
                {language === 'pt' ? 'Última execução:' : 'Last run:'} {new Date(settings.last_payout_run).toLocaleString('pt-PT')}
              </p>
            )}
          </div>
          <Button
            onClick={handleRunWeekly}
            disabled={running}
            className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
            data-testid="run-weekly-payouts-btn"
          >
            <Zap size={16} className="mr-1" />
            {running
              ? (language === 'pt' ? 'A gerar...' : 'Running...')
              : (language === 'pt' ? 'Gerar agora' : 'Run now')}
          </Button>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Building2 size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Restaurantes c/ IBAN' : 'Restaurants w/ IBAN'}</p>
                <p className="text-xl font-bold text-[#004E64]">{restaurantsWithIBAN}/{restaurants.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl bg-gradient-to-br from-yellow-50 to-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-xl">
                <Clock size={20} className="text-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Pendente' : 'Pending'}</p>
                <p className="text-xl font-bold text-[#004E64]">€{totalPending.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-xl">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Pago (período)' : 'Paid (period)'}</p>
                <p className="text-xl font-bold text-[#004E64]">€{totalCompleted.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl bg-gradient-to-br from-purple-50 to-pink-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-xl">
                <Calendar size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-[#9FA2B4]">{language === 'pt' ? 'Próximo Payout' : 'Next Payout'}</p>
                <p className="text-lg font-bold text-[#004E64]">
                  {dayOptions.find(d => d.value === settings.payout_day)?.label?.slice(0, 3)}
                </p>
                <p className="text-xs text-[#9FA2B4]">{settings.payout_time}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#F4F1EA] p-1 rounded-full">
          <TabsTrigger value="restaurants" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-4">
            <Building2 size={14} className="mr-1" />
            {language === 'pt' ? 'Restaurantes' : 'Restaurants'}
          </TabsTrigger>
          <TabsTrigger value="pending" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-4">
            <Clock size={14} className="mr-1" />
            {language === 'pt' ? 'Pendentes' : 'Pending'}
          </TabsTrigger>
          <TabsTrigger value="drivers" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-4">
            <Bike size={14} className="mr-1" />
            {language === 'pt' ? 'Estafetas' : 'Drivers'}
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-4">
            <History size={14} className="mr-1" />
            {language === 'pt' ? 'Histórico' : 'History'}
          </TabsTrigger>
          <TabsTrigger value="settings" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white text-xs px-4">
            <Settings size={14} className="mr-1" />
            {language === 'pt' ? 'Configurações' : 'Settings'}
          </TabsTrigger>
        </TabsList>

        {/* Restaurants Tab - IBAN Management */}
        <TabsContent value="restaurants" className="mt-4">
          <Card className="rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {language === 'pt' ? 'Dados Bancários dos Restaurantes' : 'Restaurant Bank Details'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {restaurants.map((restaurant) => (
                  <div 
                    key={restaurant.restaurant_id} 
                    className="border border-[#004E64]/10 rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {restaurant.logo ? (
                          <img src={restaurant.logo} alt={restaurant.name} className="w-12 h-12 rounded-xl object-cover" />
                        ) : (
                          <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center">
                            <Building2 size={20} className="text-[#FF6B35]" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-[#004E64]">{restaurant.name}</p>
                          <div className="flex items-center gap-2 text-xs text-[#9FA2B4]">
                            <CreditCard size={12} />
                            <span>IBAN: {formatIBAN(restaurant.iban)}</span>
                          </div>
                          {restaurant.company_name && (
                            <p className="text-xs text-[#9FA2B4]">{restaurant.company_name}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {restaurant.iban ? (
                          <Badge className="bg-green-100 text-green-700">
                            {language === 'pt' ? 'Configurado' : 'Configured'}
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">
                            {language === 'pt' ? 'Sem IBAN' : 'No IBAN'}
                          </Badge>
                        )}
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditForm({
                              restaurant_id: restaurant.restaurant_id,
                              name: restaurant.name,
                              iban: restaurant.iban || '',
                              company_name: restaurant.company_name || '',
                              tax_id: restaurant.tax_id || ''
                            });
                            setEditDialog({ open: true, restaurant });
                          }}
                          className="rounded-full text-xs"
                        >
                          <Edit size={14} className="mr-1" />
                          {language === 'pt' ? 'Editar' : 'Edit'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Payouts Tab */}
        <TabsContent value="pending" className="mt-4">
          <Card className="rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {language === 'pt' ? 'Pagamentos Pendentes' : 'Pending Payouts'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingPayouts.length === 0 ? (
                <div className="text-center py-8 text-[#9FA2B4]">
                  <CheckCircle size={40} className="mx-auto mb-2 text-green-500" />
                  <p>{language === 'pt' ? 'Nenhum pagamento pendente!' : 'No pending payouts!'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingPayouts.map((payout, idx) => (
                    <div
                      key={payout.payout_id || `${payout.restaurant_id}-${idx}`}
                      className="border border-[#004E64]/10 rounded-xl p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-[#004E64]">{payout.restaurant_name}</p>
                          <p className="text-xs text-[#9FA2B4]">IBAN: {formatIBAN(payout.iban)}</p>
                          <p className="text-xs text-[#9FA2B4]">{payout.orders_count} {language === 'pt' ? 'pedidos' : 'orders'}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-xl font-bold text-[#FF6B35]">€{payout.amount?.toFixed(2)}</p>
                            {getStatusBadge(payout.status)}
                          </div>
                          <Button 
                            size="sm"
                            onClick={() => setProcessDialog({ open: true, payout })}
                            className="bg-green-600 hover:bg-green-700 rounded-full text-xs"
                          >
                            <Send size={14} className="mr-1" />
                            {language === 'pt' ? 'Pagar' : 'Pay'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab with Date Filters */}
        <TabsContent value="drivers" className="mt-4">
          <Card className="rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Bike size={16} />
                {language === 'pt' ? 'Pagamentos a Estafetas (pendentes)' : 'Driver Payouts (pending)'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {driverPayouts.length === 0 ? (
                <div className="text-center py-8 text-[#9FA2B4]">
                  <CheckCircle size={40} className="mx-auto mb-2 text-green-500" />
                  <p>{language === 'pt' ? 'Nenhum pagamento pendente para estafetas.' : 'No pending driver payouts.'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {driverPayouts.map((p) => (
                    <div
                      key={p.payout_id}
                      className="border border-[#004E64]/10 rounded-xl p-4 flex items-center justify-between"
                      data-testid={`driver-payout-${p.payout_id}`}
                    >
                      <div>
                        <p className="font-semibold text-[#004E64]">{p.driver_name || p.driver_id}</p>
                        <p className="text-xs text-[#9FA2B4]">
                          {p.deliveries_count} {language === 'pt' ? 'entregas' : 'deliveries'} • {language === 'pt' ? 'taxas brutas' : 'gross fees'}: €{(p.gross_fees || 0).toFixed(2)} • {Math.round((p.share_rate || 0.8) * 100)}%
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-xl font-bold text-[#FF6B35]">€{(p.amount || 0).toFixed(2)}</p>
                          {getStatusBadge(p.status)}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => markDriverPaid(p.payout_id)}
                          className="bg-green-600 hover:bg-green-700 rounded-full text-xs"
                          data-testid={`mark-driver-paid-${p.payout_id}`}
                        >
                          <Send size={14} className="mr-1" />
                          {language === 'pt' ? 'Marcar pago' : 'Mark paid'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab with Date Filters */}
        <TabsContent value="history" className="mt-4">
          <Card className="rounded-2xl">
            <CardHeader className="pb-2">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle className="text-base">
                  {language === 'pt' ? 'Histórico de Pagamentos' : 'Payment History'}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <div>
                    <Label className="text-xs">{language === 'pt' ? 'De' : 'From'}</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="h-8 text-xs rounded-lg w-32"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{language === 'pt' ? 'Até' : 'To'}</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="h-8 text-xs rounded-lg w-32"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredHistory.length === 0 ? (
                <div className="text-center py-8 text-[#9FA2B4]">
                  <FileText size={40} className="mx-auto mb-2" />
                  <p>{language === 'pt' ? 'Nenhum pagamento no período' : 'No payments in period'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredHistory.map((payout, idx) => (
                    <div key={payout.payout_id || `${payout.restaurant_id}-${payout.period_end || idx}`} className="border border-[#004E64]/10 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-[#004E64]">{payout.restaurant_name}</p>
                          <div className="flex items-center gap-2 text-xs text-[#9FA2B4]">
                            <span>IBAN: {formatIBAN(payout.iban)}</span>
                            {payout.transfer_reference && (
                              <>
                                <span>•</span>
                                <span>Ref: {payout.transfer_reference}</span>
                              </>
                            )}
                          </div>
                          <p className="text-xs text-[#9FA2B4] mt-1">
                            {new Date(payout.processed_at || payout.created_at).toLocaleDateString('pt-PT', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-[#004E64]">€{payout.amount?.toFixed(2)}</p>
                          {getStatusBadge(payout.status)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-4">
          <Card className="rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {language === 'pt' ? 'Configurações de Pagamento' : 'Payout Settings'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold text-[#004E64] mb-3 text-sm">
                  {language === 'pt' ? 'Agendamento Semanal' : 'Weekly Schedule'}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">{language === 'pt' ? 'Dia' : 'Day'}</Label>
                    <Select
                      value={settings.payout_day}
                      onValueChange={(v) => setSettings({ ...settings, payout_day: v })}
                    >
                      <SelectTrigger className="mt-1 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {dayOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{language === 'pt' ? 'Hora' : 'Time'}</Label>
                    <Input
                      type="time"
                      value={settings.payout_time}
                      onChange={(e) => setSettings({ ...settings, payout_time: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#F4F1EA] rounded-xl">
                <div>
                  <p className="font-medium text-[#004E64]">
                    {language === 'pt' ? 'Pagamentos Automáticos' : 'Auto Payouts'}
                  </p>
                  <p className="text-xs text-[#9FA2B4]">
                    {language === 'pt' 
                      ? 'Processar automaticamente no dia/hora configurados'
                      : 'Process automatically on configured day/time'}
                  </p>
                </div>
                <Switch
                  checked={settings.auto_payout_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, auto_payout_enabled: checked })}
                />
              </div>

              <div>
                <Label className="text-xs">{language === 'pt' ? 'Valor Mínimo (€)' : 'Minimum Amount (€)'}</Label>
                <Input
                  type="number"
                  step="1"
                  value={settings.min_payout_amount}
                  onChange={(e) => setSettings({ ...settings, min_payout_amount: parseFloat(e.target.value) })}
                  className="mt-1 rounded-xl"
                />
              </div>

              <Button 
                onClick={handleSaveSettings}
                className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] rounded-xl"
              >
                <Save size={16} className="mr-2" />
                {language === 'pt' ? 'Salvar Configurações' : 'Save Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit IBAN Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, restaurant: null })}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>
              {language === 'pt' ? 'Dados Bancários' : 'Bank Details'} - {editForm.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">{language === 'pt' ? 'Nome da Empresa' : 'Company Name'}</Label>
              <Input
                value={editForm.company_name || ''}
                onChange={(e) => { const val = e.target.value; setEditForm(prev => ({ ...prev, company_name: val })); }}
                className="mt-1 rounded-xl"
                placeholder="Restaurante Lda"
              />
            </div>
            <div>
              <Label className="text-xs">NIF / {language === 'pt' ? 'Contribuinte' : 'Tax ID'}</Label>
              <Input
                value={editForm.tax_id || ''}
                onChange={(e) => { const val = e.target.value; setEditForm(prev => ({ ...prev, tax_id: val })); }}
                className="mt-1 rounded-xl"
                placeholder="123456789"
              />
            </div>
            <div>
              <Label className="text-xs">IBAN</Label>
              <Input
                value={editForm.iban || ''}
                onChange={(e) => { const val = e.target.value.toUpperCase(); setEditForm(prev => ({ ...prev, iban: val })); }}
                className="mt-1 rounded-xl font-mono"
                placeholder="PT50 0000 0000 0000 0000 0000 0"
              />
              <p className="text-xs text-[#9FA2B4] mt-1">
                {language === 'pt' ? 'IBAN português começa com PT50' : 'Portuguese IBAN starts with PT50'}
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setEditDialog({ open: false, restaurant: null })}
              >
                <X size={16} className="mr-1" />
                {language === 'pt' ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button
                className="flex-1 bg-[#FF6B35] hover:bg-[#E85D2A] rounded-xl"
                onClick={handleSaveRestaurantIBAN}
              >
                <Save size={16} className="mr-1" />
                {language === 'pt' ? 'Salvar' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Process Payout Dialog */}
      <Dialog open={processDialog.open} onOpenChange={(open) => setProcessDialog({ open, payout: null })}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>
              {language === 'pt' ? 'Processar Pagamento' : 'Process Payout'}
            </DialogTitle>
          </DialogHeader>
          {processDialog.payout && (
            <div className="space-y-4">
              <div className="p-4 bg-[#F4F1EA] rounded-xl">
                <p className="font-semibold text-[#004E64]">{processDialog.payout.restaurant_name}</p>
                <p className="text-sm text-[#9FA2B4]">IBAN: {processDialog.payout.iban}</p>
                <p className="text-2xl font-bold text-[#FF6B35] mt-2">€{processDialog.payout.amount?.toFixed(2)}</p>
              </div>
              
              <div>
                <Label className="text-xs">{language === 'pt' ? 'Referência da Transferência SEPA' : 'SEPA Transfer Reference'}</Label>
                <Input
                  value={transferRef}
                  onChange={(e) => setTransferRef(e.target.value)}
                  className="mt-1 rounded-xl"
                  placeholder="TRF-2024-001"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => setProcessDialog({ open: false, payout: null })}
                >
                  {language === 'pt' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
                  onClick={() => handleProcessPayout(processDialog.payout)}
                >
                  <CheckCircle size={16} className="mr-1" />
                  {language === 'pt' ? 'Confirmar' : 'Confirm'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayoutManagement;
